import {
  PropsWithChildren,
  createContext,
  useContext,
  useReducer,
} from 'react';

import {
  ActionTypes,
  AddMetamodelTypePayload,
  ByIdMetamodelTypePayload,
  EditMetamodelTypePayload,
  EditorErrorPayload,
  MetamodelEditorState,
  MetamodelHierarchy,
  ValidateNameFunction,
} from './types';
import { ArdoqId } from '@ardoq/api-types';
import {
  handleAddMetamodelType,
  handleDeleteMetamodelType,
  handleEditMetamodelType,
  handleSetIsFlexible,
  handleSetSelectedMetamodelType,
} from './reducerHelpers';
import {
  ReducerActions,
  addMetamodelTypeAction,
  deleteMetamodelTypeAction,
  editMetamodelTypeAction,
  setEditorErrorAction,
  setIsFlexibleAction,
  setIsLoadingAction,
  setSelectedNodeIdAction,
} from './actions';
import { ROOT_ID, decomposeMetamodel, validateNameFromRecord } from './utils';
import { MetamodelEditorLabels, mergeMetamodelEditorLabels } from '../labels';

/**
 * Used for initial Editor State, passed as props
 */
interface MetamodelEditorStateProviderProps {
  initialMetamodel: MetamodelHierarchy;
  isFlexible?: boolean;
  preselectedMetamodelTypeId?: ArdoqId;
  usedMetamodelTypeIds?: Set<ArdoqId>;
  /**
   * Optional partial overrides for user-facing strings. Missing keys fall
   * back to English defaults so this package stays translation-free
   * (Pattern A).
   */
  labels?: Partial<MetamodelEditorLabels>;
}

/**
 * The Provided State including the Actions
 */
interface MetamodelEditorContextState extends MetamodelEditorState {
  editMetamodelType: (payload: EditMetamodelTypePayload) => void;
  addMetamodelType: (payload: AddMetamodelTypePayload) => void;
  deleteMetamodelType: (payload: ByIdMetamodelTypePayload) => void;
  setIsFlexible: (payload: boolean) => void;
  setIsLoading: (payload: boolean) => void;
  setEditorError: (payload: EditorErrorPayload) => void;
  setSelectedMetamodelTypeId: (payload: ByIdMetamodelTypePayload) => void;
  // Tree Context
  usedMetamodelTypeIds?: Set<ArdoqId>;

  validateName: ValidateNameFunction;
  /**
   * Resolved user-facing strings (defaults merged with caller overrides).
   * Lives on the context value rather than the reducer state so a parent
   * re-render with updated `labelOverrides` (e.g. locale switch
   * mid-session) is reflected immediately. `useReducer`'s initializer
   * only runs on mount, so storing labels in reducer state would freeze
   * them at the first render.
   */
  labels: MetamodelEditorLabels;
}

const stateInitializer = ({
  initialMetamodel = {},
  isFlexible = false,
  preselectedMetamodelTypeId,
}: Partial<MetamodelEditorStateProviderProps>): MetamodelEditorState => {
  const decomposedMetamodel = decomposeMetamodel(initialMetamodel);

  const selectedMetamodelTypeId =
    preselectedMetamodelTypeId ||
    decomposedMetamodel.childrenIndexes[ROOT_ID][0];

  const selectedMetamodelType = selectedMetamodelTypeId
    ? decomposedMetamodel.metamodelTypes[selectedMetamodelTypeId]
    : undefined;

  return {
    persistedDecomposedMetamodel: decomposedMetamodel,
    decomposedMetamodel: decomposedMetamodel,
    hasChanges: false,
    isLoading: false,
    isFlexible: isFlexible,
    editorError: null,
    selectedMetamodelTypeId: selectedMetamodelTypeId,
    selectedMetamodelType: selectedMetamodelType,
  };
};

/* ********************************************************************************
 ------------------------------------  Reducer  -----------------------------------
 **********************************************************************************/
const reducer = (
  state: MetamodelEditorState,
  { type, payload }: ReducerActions
): MetamodelEditorState => {
  switch (type) {
    case ActionTypes.ADD_MODEL_TYPE:
      return handleAddMetamodelType(state, payload);
    case ActionTypes.DELETE_MODEL_TYPE:
      return handleSetSelectedMetamodelType(
        handleDeleteMetamodelType(state, payload),
        {}
      );
    case ActionTypes.EDIT_MODEL_TYPE:
      return handleSetSelectedMetamodelType(
        handleEditMetamodelType(state, payload),
        { id: state.selectedMetamodelTypeId }
      );
    case ActionTypes.SET_IS_FLEXIBLE:
      return handleSetIsFlexible(state, payload);
    case ActionTypes.SET_SELECTED_NODE_ID:
      return handleSetSelectedMetamodelType(state, payload);

    case ActionTypes.SET_IS_LOADING:
      return { ...state, isLoading: payload.is };
    case ActionTypes.SET_EDITOR_ERROR:
      return { ...state, editorError: payload };
    default:
      return state;
  }
};

/* ********************************************************************************
 ------------------------------------  Context  -----------------------------------
 **********************************************************************************/

const metaModelEditorContext = createContext<
  MetamodelEditorContextState | undefined
>(undefined);

export const useMetamodelEditorContext = () => {
  const state = useContext(metaModelEditorContext);
  if (!state) {
    throw new Error(
      '"useMetamodelEditorContext" used outside "metaModelEditorContext"'
    );
  }
  return state;
};
export const MetamodelEditorStateProvider = ({
  children,
  usedMetamodelTypeIds,
  labels: labelOverrides,
  ...props
}: PropsWithChildren<MetamodelEditorStateProviderProps>) => {
  const labels = mergeMetamodelEditorLabels(labelOverrides);
  const [state, dispatchContextAction] = useReducer(
    reducer,
    props,
    stateInitializer
  );

  const editMetamodelType = (payload: EditMetamodelTypePayload) =>
    dispatchContextAction(
      editMetamodelTypeAction({
        ...payload,
        validationLabels: {
          validationNameEmpty: labels.validationNameEmpty,
          validationNameTaken: labels.validationNameTaken,
        },
      })
    );
  const addMetamodelType = (payload: AddMetamodelTypePayload) =>
    dispatchContextAction(addMetamodelTypeAction(payload));
  const deleteMetamodelType = (payload: ByIdMetamodelTypePayload) =>
    dispatchContextAction(deleteMetamodelTypeAction(payload));
  const setIsFlexible = (payload: boolean) =>
    dispatchContextAction(setIsFlexibleAction({ is: payload }));
  const setIsLoading = (payload: boolean) =>
    dispatchContextAction(setIsLoadingAction({ is: payload }));
  const setEditorError = (payload: EditorErrorPayload) =>
    dispatchContextAction(setEditorErrorAction(payload));
  const setSelectedMetamodelTypeId = (
    payload: Partial<ByIdMetamodelTypePayload>
  ) => dispatchContextAction(setSelectedNodeIdAction(payload));

  const validateName: ValidateNameFunction = metamodelType =>
    validateNameFromRecord(
      state.decomposedMetamodel.metamodelTypes,
      metamodelType,
      labels
    );

  return (
    <metaModelEditorContext.Provider
      value={{
        ...state,
        editMetamodelType,
        addMetamodelType,
        deleteMetamodelType,
        setIsLoading,
        setIsFlexible,
        setEditorError,
        setSelectedMetamodelTypeId,
        validateName,
        usedMetamodelTypeIds,
        labels,
      }}
    >
      {children}
    </metaModelEditorContext.Provider>
  );
};
